import type { ProvisioningStep } from '@flashpoint/site-dna';
import type { ProvisionStreamEvent } from '../lib/orchestratorClient';

export type DeployPhase = 'idle' | 'building' | 'live' | 'failed';

export function deriveDeployPhase(
  streamLog: ProvisionStreamEvent[],
  running: boolean,
  lastErr: string | null,
): DeployPhase {
  if (lastErr) return 'failed';

  const cfEvents = streamLog.filter((ev) => ev.step === 'cloudflare');
  const lastCf = cfEvents[cfEvents.length - 1];
  if (lastCf?.status === 'error') return 'failed';
  if (lastCf?.status === 'ok') return 'live';
  if (lastCf?.status === 'in_progress' || (running && streamLog.some((ev) => ev.step === 'github' && ev.status === 'ok'))) {
    if (running || lastCf?.status === 'in_progress') return 'building';
  }
  return 'idle';
}

export function lastCloudflareHeadSha(streamLog: ProvisionStreamEvent[]): string | undefined {
  for (let i = streamLog.length - 1; i >= 0; i -= 1) {
    const ev = streamLog[i];
    if (!ev || ev.step !== 'cloudflare' || ev.status !== 'ok') continue;
    const sha = ev.data?.headSha;
    if (typeof sha === 'string' && sha.trim()) return sha.trim();
  }
  return undefined;
}

export function lastCloudflareMessage(streamLog: ProvisionStreamEvent[]): string | undefined {
  for (let i = streamLog.length - 1; i >= 0; i -= 1) {
    const ev = streamLog[i];
    if (!ev || ev.step !== 'cloudflare') continue;
    if (ev.message?.trim()) return ev.message.trim();
  }
  return undefined;
}

function lastCloudflareStep(steps: ProvisioningStep[]): ProvisioningStep | undefined {
  for (let i = steps.length - 1; i >= 0; i -= 1) {
    if (steps[i]?.name === 'cloudflare') return steps[i];
  }
  return undefined;
}

/** User-facing failure label when generate/provision errors. */
export function deriveDeployFailureLabel(
  streamLog: ProvisionStreamEvent[],
  lastErr: string | null,
): string {
  if (!lastErr) return 'Preview deploy failed';

  const lastGithub = [...streamLog].reverse().find((ev) => ev.step === 'github');
  const githubReached = lastGithub?.status === 'in_progress' || lastGithub?.status === 'ok';
  const cloudflareStarted = streamLog.some((ev) => ev.step === 'cloudflare');

  if (githubReached && !cloudflareStarted) {
    return 'GitHub sync failed';
  }
  return 'Preview deploy failed';
}

interface Props {
  streamLog: ProvisionStreamEvent[];
  running: boolean;
  lastErr?: string | null;
  provisioningSteps?: ProvisioningStep[];
}

export function DeployPreviewStatus({ streamLog, running, lastErr, provisioningSteps = [] }: Props) {
  const phase = deriveDeployPhase(streamLog, running, lastErr ?? null);
  const message = lastCloudflareMessage(streamLog);
  const headSha = lastCloudflareHeadSha(streamLog);
  const persisted = lastCloudflareStep(provisioningSteps);

  if (phase === 'idle') {
    if (!persisted || persisted.status !== 'ok') return null;
    return (
      <p className="mt-1 text-xs text-nm-muted">
        Last preview sync{ persisted.finishedAt ? ` · ${new Date(persisted.finishedAt).toLocaleString()}` : '' }
      </p>
    );
  }

  if (phase === 'failed') {
    const failureLabel = deriveDeployFailureLabel(streamLog, lastErr ?? null);
    return (
      <div className="mt-1 rounded-lg border border-nm-danger/30 bg-nm-danger-bg px-3 py-2 text-xs text-nm-danger">
        {failureLabel}{lastErr ? ` — ${lastErr}` : ''}
      </div>
    );
  }

  if (phase === 'building') {
    return (
      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-nm-warn/15 px-2.5 py-1 font-semibold text-nm-warn">
          <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-nm-warn" aria-hidden />
          Preview updating…
        </span>
        {message && <span className="text-nm-muted">{message}</span>}
      </div>
    );
  }

  return (
    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
      <span className="rounded-full bg-nm-success/15 px-2.5 py-1 font-semibold text-nm-success">
        Preview ready
      </span>
      {headSha && (
        <span className="font-mono text-nm-muted">{headSha.slice(0, 8)}</span>
      )}
    </div>
  );
}

export function ProvisionStreamLine({ ev }: { ev: ProvisionStreamEvent }) {
  const statusClass =
    ev.status === 'ok'
      ? 'text-nm-success'
      : ev.status === 'in_progress'
        ? 'text-nm-warn'
        : ev.status === 'error'
          ? 'text-nm-danger'
          : 'text-nm-ink';

  return (
    <div className={statusClass}>
      <span className="text-nm-muted">{ev.step}</span> {ev.status}
      {ev.message ? ` — ${ev.message}` : ''}
    </div>
  );
}
